import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api';

import API_URL from '../api';
const API_BASE = API_URL;

const ESTADO_FACTURA = {
  pendiente:    { bg: '#2a2a1a', color: '#ffc107', label: '⏳ Pendiente'    },
  verificada:   { bg: '#1a3a2a', color: '#4caf50', label: '✅ Verificada'   },
  diferencias:  { bg: '#3a1a1a', color: '#e63946', label: '⚠️ Diferencias'  },
  sin_registro: { bg: '#2a1a3a', color: '#9c27b0', label: '🔍 Sin registro' },
};

const ESTADO_ITEM = {
  pendiente:     { color: '#555',    label: '—'            },
  ok:            { color: '#4caf50', label: '✅ OK'         },
  diff_costo:    { color: '#ffc107', label: '⚠️ Costo'      },
  diff_cantidad: { color: '#ff9800', label: '⚠️ Cantidad'   },
  diff_ambos:    { color: '#e63946', label: '❌ Costo+Cant.' },
  faltante:      { color: '#e63946', label: '❌ Faltante'   },
};

function OrigenBadge({ origen }) {
  if (!origen) return null;
  const cfg = {
    xml:     { bg: '#1a2a3e', color: '#4cc9f0', label: 'XML'      },
    pdf:     { bg: '#2a1a2a', color: '#f72585', label: 'PDF'      },
    sri_bot: { bg: '#1a3a1a', color: '#4caf50', label: '🤖 SRI'   },
    gmail:   { bg: '#2a1a0a', color: '#ff9800', label: '📧 GMAIL' },
  };
  const { bg, color, label } = cfg[origen] || { bg: '#2a2a2a', color: '#aaa', label: origen.toUpperCase() };
  return (
    <span style={{ background: bg, color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 'bold', marginLeft: 6 }}>
      {label}
    </span>
  );
}

// ─── Modal Re-validación masiva ───────────────────────────────────────────────
function ModalRevalidacion({ facturas, onClose, onCompletado }) {
  const REVALIDABLES = ['pendiente', 'sin_registro', 'diferencias'];
  const candidatas   = facturas.filter(f =>
    REVALIDABLES.includes(f.estado) &&
    !(f.estado === 'diferencias' && f.ingreso_verificado)
  );

  const [iniciado,  setIniciado]  = useState(false);
  const [terminado, setTerminado] = useState(false);
  const [progreso,  setProgreso]  = useState(0);
  const [actual,    setActual]    = useState(null);
  const [log,       setLog]       = useState([]);
  const abortRef                  = useRef({ abort: false });

  const resumen = useMemo(() => ({
    verificadas:  log.filter(r => r.estado === 'verificada').length,
    diferencias:  log.filter(r => r.estado === 'diferencias').length,
    sin_registro: log.filter(r => r.estado === 'sin_registro').length,
    error_ocr:    log.filter(r => r.estado === 'error_ocr').length,
    errores:      log.filter(r => r.estado === 'error').length,
  }), [log]);

  const iniciar = useCallback(async () => {
    if (candidatas.length === 0) return;
    setIniciado(true);
    abortRef.current.abort = false;

    for (let i = 0; i < candidatas.length; i++) {
      if (abortRef.current.abort) break;
      const f = candidatas[i];
      setActual(f);
      setProgreso(Math.round((i / candidatas.length) * 100));

      try {
        const resp = await api.post(`/facturas/${f.id}/verificar`, {});
        setLog(prev => [...prev, {
          id:        f.id,
          numero:    f.numero_factura || `#${f.id}`,
          proveedor: f.nombre_proveedor || '',
          estado:    resp.estado || (resp.ok ? 'verificada' : 'error'),
          ok:        resp.ok || false,
        }]);
      } catch {
        setLog(prev => [...prev, {
          id:        f.id,
          numero:    f.numero_factura || `#${f.id}`,
          proveedor: f.nombre_proveedor || '',
          estado:    'error',
          ok:        false,
        }]);
      }
      if (i < candidatas.length - 1) {
        await new Promise(r => setTimeout(r, 400));
      }
    }
    setProgreso(100);
    setActual(null);
    setTerminado(true);
  }, [candidatas]);

  function detener() {
    abortRef.current.abort = true;
    setTerminado(true);
    setActual(null);
  }

  const COLOR_ESTADO = {
    verificada:   '#4caf50',
    diferencias:  '#ffc107',
    sin_registro: '#9c27b0',
    error_ocr:    '#f72585',
    error:        '#555',
  };
  const ICON_ESTADO = {
    verificada:   '✅',
    diferencias:  '⚠️',
    sin_registro: '🔍',
    error_ocr:    '🔴',
    error:        '💥',
  };

  return (
    <>
      <div onClick={!iniciado || terminado ? onClose : undefined}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)',
        background:'#1a1a2e', borderRadius:14,
        width:580, maxHeight:'85vh',
        zIndex:301, border:'1px solid #2d2d44',
        boxShadow:'0 20px 60px rgba(0,0,0,0.7)',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
        {/* Header */}
        <div style={{ padding:'16px 22px 14px',
          borderBottom:'1px solid #2d2d44',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>
              🔄 Re-validación masiva
            </div>
            <div style={{ color:'#555', fontSize:11, marginTop:3 }}>
              {candidatas.length} facturas · pendiente · sin_registro · diferencias
            </div>
          </div>
          {(!iniciado || terminado) && (
            <button onClick={onClose} style={{ background:'none', border:'none',
              color:'#555', fontSize:20, cursor:'pointer' }}>✕</button>
          )}
        </div>

        {/* Cuerpo */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>

          {/* Estado inicial */}
          {!iniciado && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {candidatas.length === 0 ? (
                <div style={{ color:'#4caf50', textAlign:'center', padding:30, fontSize:14 }}>
                  ✅ Todas las facturas ya están verificadas.
                </div>
              ) : (
                <>
                  <div style={{ background:'#0f0f1a', borderRadius:9, padding:'12px 16px' }}>
                    <div style={{ color:'#aaa', fontSize:12, marginBottom:8 }}>
                      Facturas a procesar:
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {REVALIDABLES.map(est => {
                        const cant = facturas.filter(f => f.estado === est).length;
                        if (!cant) return null;
                        return (
                          <span key={est} style={{ background:'#2a2a3e',
                            borderRadius:6, padding:'4px 10px',
                            fontSize:12, color:'#aaa' }}>
                            {ICON_ESTADO[est]} {est}:{' '}
                            <strong style={{ color:'#fff' }}>{cant}</strong>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ color:'#555', fontSize:12, lineHeight:1.6 }}>
                    Proceso secuencial con pausa de 400ms entre cada factura.
                    Tiempo estimado:{' '}
                    <strong style={{ color:'#aaa' }}>
                      ~{Math.ceil(candidatas.length * 0.5)}s
                    </strong>
                  </div>
                  <button onClick={iniciar} style={{
                    background:'#4361ee', border:'none', borderRadius:8,
                    color:'#fff', padding:'11px 0',
                    fontSize:14, fontWeight:700, cursor:'pointer',
                  }}>
                    🚀 Iniciar re-validación ({candidatas.length} facturas)
                  </button>
                </>
              )}
            </div>
          )}

          {/* En progreso */}
          {iniciado && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* Barra */}
              <div>
                <div style={{ display:'flex', justifyContent:'space-between',
                  marginBottom:5 }}>
                  <span style={{ color:'#aaa', fontSize:12 }}>
                    {terminado
                      ? `Completado — ${log.length} procesadas`
                      : `Procesando ${log.length + 1} de ${candidatas.length}...`}
                  </span>
                  <span style={{ color:'#4361ee', fontFamily:'monospace',
                    fontSize:12, fontWeight:700 }}>{progreso}%</span>
                </div>
                <div style={{ height:8, background:'#2a2a3e',
                  borderRadius:4, overflow:'hidden' }}>
                  <div style={{
                    width:`${progreso}%`, height:'100%',
                    background: terminado ? '#4caf50' : '#4361ee',
                    borderRadius:4, transition:'width .3s',
                  }} />
                </div>
                {actual && !terminado && (
                  <div style={{ color:'#555', fontSize:11, marginTop:4 }}>
                    → {actual.numero_factura || `#${actual.id}`}
                    {actual.nombre_proveedor && ` · ${actual.nombre_proveedor}`}
                  </div>
                )}
              </div>

              {/* KPIs en tiempo real */}
              {log.length > 0 && (
                <div style={{ display:'grid',
                  gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
                  {[
                    { label:'✅ OK',       val:resumen.verificadas,  color:'#4caf50' },
                    { label:'⚠️ Difs.',    val:resumen.diferencias,  color:'#ffc107' },
                    { label:'🔍 Sin reg.', val:resumen.sin_registro, color:'#9c27b0' },
                    { label:'🔴 OCR err.', val:resumen.error_ocr,    color:'#f72585' },
                    { label:'💥 Errores',  val:resumen.errores,      color:'#555'    },
                  ].map(k => (
                    <div key={k.label} style={{ background:'#0f0f1a',
                      borderRadius:7, padding:'8px 6px', textAlign:'center' }}>
                      <div style={{ color:k.color, fontWeight:700,
                        fontSize:18, fontFamily:'monospace' }}>{k.val}</div>
                      <div style={{ color:'#555', fontSize:9, marginTop:2 }}>
                        {k.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Log */}
              <div style={{ background:'#0f0f1a', borderRadius:9,
                maxHeight:260, overflowY:'auto' }}>
                {log.length === 0 ? (
                  <div style={{ color:'#555', padding:20,
                    textAlign:'center', fontSize:12 }}>
                    Procesando primera factura...
                  </div>
                ) : (
                  [...log].reverse().map((r, i) => (
                    <div key={r.id} style={{
                      display:'flex', alignItems:'center', gap:10,
                      padding:'8px 14px',
                      borderBottom:'1px solid #1a1a2e',
                      background: i === 0 ? '#1a1a2e' : 'transparent',
                    }}>
                      <span style={{ fontSize:14, flexShrink:0 }}>
                        {ICON_ESTADO[r.estado] || '•'}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ color:COLOR_ESTADO[r.estado] || '#aaa',
                          fontSize:11, fontFamily:'monospace',
                          whiteSpace:'nowrap', overflow:'hidden',
                          textOverflow:'ellipsis' }}>
                          {r.numero}
                        </div>
                        {r.proveedor && (
                          <div style={{ color:'#444', fontSize:10,
                            whiteSpace:'nowrap', overflow:'hidden',
                            textOverflow:'ellipsis' }}>{r.proveedor}</div>
                        )}
                      </div>
                      <span style={{ color:COLOR_ESTADO[r.estado] || '#555',
                        fontSize:10, flexShrink:0,
                        textTransform:'uppercase', fontWeight:700 }}>
                        {r.estado}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Botones */}
              <div style={{ display:'flex', gap:8 }}>
                {!terminado && (
                  <button onClick={detener} style={{
                    flex:1, background:'#2a0d0d',
                    border:'1px solid #e6394655',
                    borderRadius:8, color:'#e63946',
                    padding:'9px 0', fontSize:13,
                    fontWeight:700, cursor:'pointer',
                  }}>⏹ Detener</button>
                )}
                {terminado && (
                  <>
                    <button onClick={() => { onCompletado(); onClose(); }} style={{
                      flex:1, background:'#4caf50', border:'none',
                      borderRadius:8, color:'#fff', padding:'9px 0',
                      fontSize:13, fontWeight:700, cursor:'pointer',
                    }}>✅ Listo — actualizar lista</button>
                    <button onClick={onClose} style={{
                      background:'none', border:'1px solid #2d2d44',
                      borderRadius:8, color:'#aaa', padding:'9px 18px',
                      fontSize:13, cursor:'pointer',
                    }}>Cerrar</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Facturas() {
  const [vista, setVista]                   = useState('lista');
  const [facturas, setFacturas]             = useState([]);
  const [facturaActual, setFacturaActual]   = useState(null);
  const [cargando, setCargando]             = useState(true);
  const [subiendo, setSubiendo]             = useState(false);
  const [verificando, setVerificando]       = useState(false);
  const [modalMorpheus, setModalMorpheus]         = useState(null);  // null | datos
  const [cargandoMorpheus, setCargandoMorpheus]   = useState(false);
  const [resultadoCarga, setResultadoCarga] = useState(null);
  const [errorMsg, setErrorMsg]             = useState('');
  const [dragging, setDragging]             = useState(false);
  const [pagina, setPagina]                 = useState(1);
  const [modalReval, setModalReval]         = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { cargarFacturas(); }, []);

  const cargarFacturas = async () => {
    setCargando(true);
    try {
      const data = await api.get('/facturas');
      setFacturas(data.facturas || []);
      setPagina(1);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  };

  const subirArchivos = async (archivos) => {
    if (!archivos || archivos.length === 0) return;
    const invalidos = Array.from(archivos).filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return !['xml', 'pdf'].includes(ext);
    });
    if (invalidos.length > 0) {
      setErrorMsg(`Formato no soportado: ${invalidos.map(f => f.name).join(', ')}`);
      return;
    }
    setSubiendo(true);
    setErrorMsg('');
    setResultadoCarga(null);
    try {
      const formData = new FormData();
      Array.from(archivos).forEach(f => formData.append('archivos', f));
      const res  = await fetch(`${API_BASE}/facturas/ocr`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) {
        setErrorMsg(data.error);
      } else {
        setResultadoCarga(data);
        await cargarFacturas();
        if (data.resultados?.length === 1 && data.resultados[0].estado === 'nueva') {
          verDetalle(data.resultados[0].factura_id);
        }
      }
    } catch (e) {
      setErrorMsg('Error de conexión con el servidor');
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const verDetalle = async (id) => {
    setCargando(true);
    try {
      const data = await api.get(`/facturas/${id}`);
      if (!data.error) { setFacturaActual(data); setVista('detalle'); }
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  };

  const verificar = async () => {
    if (!facturaActual) return;
    setVerificando(true);
    setErrorMsg('');
    try {
      const data = await api.post(`/facturas/${facturaActual.factura.id}/verificar`, {});
      if (data.error) { setErrorMsg(data.error); }
      else { await verDetalle(facturaActual.factura.id); await cargarFacturas(); }
    } catch (e) { setErrorMsg('Error verificando contra Morpheus'); }
    finally { setVerificando(false); }
  };

  const verEnMorpheus = async () => {
    if (!facturaActual) return;
    setCargandoMorpheus(true);
    setModalMorpheus(null);
    try {
        const data = await api.get(`/facturas/${facturaActual.factura.id}/morpheus`);
        setModalMorpheus(data);
    } catch (e) {
        setModalMorpheus({ error: 'Error de conexión' });
    } finally {
        setCargandoMorpheus(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta factura del sistema?')) return;
    try {
      await api.delete(`/facturas/${id}`);
      setVista('lista'); setFacturaActual(null); cargarFacturas();
    } catch (e) { console.error(e); }
  };

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()  => setDragging(false);
  const onDrop      = (e) => { e.preventDefault(); setDragging(false); subirArchivos(e.dataTransfer.files); };

  // ════ VISTA DETALLE ════
  if (vista === 'detalle' && facturaActual) {
    const { factura, items } = facturaActual;
    const estadoF        = ESTADO_FACTURA[factura.estado] || ESTADO_FACTURA.pendiente;
    const itemsSinBonif  = items.filter(i => !i.es_bonificacion);
    const bonificaciones = items.filter(i => i.es_bonificacion);
    const faltantes      = itemsSinBonif.filter(i => i.estado_item === 'faltante').length;
    const conDiff        = itemsSinBonif.filter(i => ['diff_costo','diff_cantidad','diff_ambos'].includes(i.estado_item)).length;
    const okCount        = itemsSinBonif.filter(i => i.estado_item === 'ok').length;
    const origen         = (factura.notas || '').replace('origen:', '');
    const yaVerificado   = ['verificada','diferencias'].includes(factura.estado);

    return (
      <div>
        <div style={s.headerRow}>
          <div>
            <button style={s.botonVolver} onClick={() => { setVista('lista'); setFacturaActual(null); setErrorMsg(''); }}>
              ← Volver
            </button>
            <h2 style={{ ...s.titulo, marginTop: 12 }}>
                <span
                    style={{ cursor: 'pointer', borderBottom: '1px dashed #4cc9f0', paddingBottom: 1 }}
                    onClick={verEnMorpheus}
                    title="Ver datos en Morpheus"
                >
                    📄 {factura.numero_factura}
                </span>
                <OrigenBadge origen={origen} />
                {cargandoMorpheus && (
                    <span style={{ color: '#555', fontSize: 12, marginLeft: 12 }}>cargando Morpheus...</span>
                )}
            </h2>
            <p style={s.subtitulo}>
              {factura.nombre_proveedor} · RUC: {factura.ruc_proveedor}
              {factura.fecha_factura && ` · ${factura.fecha_factura}`}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <span style={{ ...s.estadoBadge, background: estadoF.bg, color: estadoF.color }}>
              {estadoF.label}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...s.botonVerificar, ...(verificando ? { opacity: 0.6 } : {}) }}
                onClick={verificar} disabled={verificando}>
                {verificando ? '🔍 Verificando...' : yaVerificado ? '🔄 Re-verificar' : '🔍 Verificar en Morpheus'}
              </button>
              <button style={s.botonEliminar} onClick={() => eliminar(factura.id)}>🗑️</button>
            </div>
          </div>
{modalMorpheus && (
    <div style={sModal.overlay} onClick={() => setModalMorpheus(null)}>
        <div style={sModal.modal} onClick={e => e.stopPropagation()}>
 
            {/* Header */}
            <div style={sModal.header}>
                <div>
                    <div style={sModal.titulo}>🏭 Datos en Morpheus</div>
                    <div style={sModal.subtitulo}>{modalMorpheus.numero_factura}</div>
                </div>
                <button style={sModal.cerrar} onClick={() => setModalMorpheus(null)}>✕</button>
            </div>
 
            {/* Contenido */}
            {modalMorpheus.error ? (
                <div style={{ padding: 24, color: '#e63946' }}>{modalMorpheus.error}</div>
            ) : !modalMorpheus.encontrado ? (
                <div style={{ padding: 24, color: '#ffc107' }}>
                    🔍 {modalMorpheus.mensaje}
                </div>
            ) : (
                <div style={sModal.body}>
                    {/* Info cabecera */}
                    <div style={sModal.infoGrid}>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>PROVEEDOR</div>
                            <div style={sModal.infoValor}>{modalMorpheus.proveedor}</div>
                        </div>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>RUC</div>
                            <div style={sModal.infoValor}>{modalMorpheus.ruc}</div>
                        </div>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>FECHA</div>
                            <div style={sModal.infoValor}>{modalMorpheus.fecha}</div>
                        </div>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>FORMA PAGO</div>
                            <div style={sModal.infoValor}>{modalMorpheus.forma_pago || '—'}</div>
                        </div>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>INGRESADO POR</div>
                            <div style={sModal.infoValor}>{modalMorpheus.usuario || '—'}</div>
                        </div>
                        <div style={sModal.infoItem}>
                            <div style={sModal.infoLabel}>PUNTO DE VENTA</div>
                            <div style={sModal.infoValor}>{modalMorpheus.punto_venta || '—'}</div>
                        </div>
                    </div>
 
                    {/* Totales */}
                    <div style={sModal.totalesRow}>
                        <div style={sModal.totalItem}>
                            <div style={sModal.infoLabel}>SUBTOTAL 12%</div>
                            <div style={{ color: '#aaa', fontSize: 15 }}>${modalMorpheus.subtotal12.toFixed(2)}</div>
                        </div>
                        <div style={sModal.totalItem}>
                            <div style={sModal.infoLabel}>IVA {modalMorpheus.iva_pct}%</div>
                            <div style={{ color: '#aaa', fontSize: 15 }}>${modalMorpheus.iva.toFixed(2)}</div>
                        </div>
                        <div style={sModal.totalItem}>
                            <div style={sModal.infoLabel}>TOTAL</div>
                            <div style={{ color: '#4cc9f0', fontSize: 18, fontWeight: 'bold' }}>${modalMorpheus.total.toFixed(2)}</div>
                        </div>
                    </div>
 
                    {/* Tabla ítems */}
                    <div style={sModal.tablaWrap}>
                        <div style={sModal.tablaHeader}>
                            <span style={{ width: 120 }}>CÓDIGO</span>
                            <span style={{ flex: 4 }}>DESCRIPCIÓN</span>
                            <span style={{ width: 70, textAlign: 'right' }}>CANT.</span>
                            <span style={{ width: 110, textAlign: 'right' }}>COSTO S/IVA</span>
                            <span style={{ width: 100, textAlign: 'right' }}>TOTAL</span>
                        </div>
                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                            {modalMorpheus.items.map((item, i) => (
                                <div key={i} style={{
                                    display: 'flex', padding: '10px 16px', gap: 8,
                                    alignItems: 'center',
                                    background: i % 2 === 0 ? '#1a1a2e' : '#1e1e2e',
                                    borderBottom: '1px solid #0f0f1a'
                                }}>
                                    <span style={{ width: 120, color: '#4cc9f0', fontSize: 12, fontWeight: 'bold' }}>
                                        {item.codigo_bar || '—'}
                                    </span>
                                    <span style={{ flex: 4, color: '#fff', fontSize: 12 }}>{item.descripcion}</span>
                                    <span style={{ width: 70, textAlign: 'right', color: '#fff', fontWeight: 'bold' }}>
                                        {item.cantidad.toFixed(0)}
                                    </span>
                                    <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>
                                        ${item.precio_sin_iva.toFixed(4)}
                                    </span>
                                    <span style={{ width: 100, textAlign: 'right', color: '#4cc9f0' }}>
                                        ${item.total_linea.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
)}
        </div>

        {errorMsg && <div style={s.errorBox}>{errorMsg}</div>}

        <div style={s.kpisRow}>
          <div style={{ ...s.kpiCard, borderTopColor: '#4cc9f0' }}>
            <div style={{ ...s.kpiValor, color: '#4cc9f0' }}>${factura.total.toFixed(2)}</div>
            <div style={s.kpiLabel}>Total factura</div>
          </div>
          <div style={{ ...s.kpiCard, borderTopColor: '#555' }}>
            <div style={{ ...s.kpiValor, color: '#aaa' }}>${factura.subtotal12.toFixed(2)}</div>
            <div style={s.kpiLabel}>Subtotal 12%</div>
          </div>
          <div style={{ ...s.kpiCard, borderTopColor: '#7209b7' }}>
            <div style={{ ...s.kpiValor, color: '#7209b7' }}>{itemsSinBonif.length}</div>
            <div style={s.kpiLabel}>Ítems</div>
          </div>
          {bonificaciones.length > 0 && (
            <div style={{ ...s.kpiCard, borderTopColor: '#4caf50' }}>
              <div style={{ ...s.kpiValor, color: '#4caf50' }}>{bonificaciones.length}</div>
              <div style={s.kpiLabel}>Bonificaciones</div>
            </div>
          )}
          {yaVerificado && <>
            <div style={{ ...s.kpiCard, borderTopColor: '#4caf50' }}>
              <div style={{ ...s.kpiValor, color: '#4caf50' }}>{okCount}</div>
              <div style={s.kpiLabel}>✅ Correctos</div>
            </div>
            <div style={{ ...s.kpiCard, borderTopColor: '#ffc107' }}>
              <div style={{ ...s.kpiValor, color: '#ffc107' }}>{conDiff}</div>
              <div style={s.kpiLabel}>⚠️ Diferencias</div>
            </div>
            <div style={{ ...s.kpiCard, borderTopColor: '#e63946' }}>
              <div style={{ ...s.kpiValor, color: '#e63946' }}>{faltantes}</div>
              <div style={s.kpiLabel}>❌ Faltantes</div>
            </div>
          </>}
        </div>

        {factura.estado === 'sin_registro' && (
          <div style={s.alertBox}>
            🔍 Factura <strong>no encontrada en Morpheus</strong>. Ingresa la compra y luego presiona Re-verificar.
          </div>
        )}

        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitulo}>📦 Ítems de la factura</span>
            {factura.fecha_verificacion && (
              <span style={{ color: '#555', fontSize: 11 }}>Verificado: {factura.fecha_verificacion}</span>
            )}
          </div>
          <div style={s.tabla}>
            <div style={s.tablaHeader}>
              <span style={{ width: 120 }}>CÓDIGO</span>
              <span style={{ flex: 4 }}>DESCRIPCIÓN</span>
              <span style={{ width: 70,  textAlign: 'right' }}>CANT.</span>
              <span style={{ width: 105, textAlign: 'right' }}>COSTO UNIT.</span>
              <span style={{ width: 100, textAlign: 'right' }}>TOTAL</span>
              {yaVerificado && <>
                <span style={{ width: 85,  textAlign: 'right'  }}>CANT. MRP</span>
                <span style={{ width: 110, textAlign: 'right'  }}>COSTO MRP</span>
                <span style={{ width: 115, textAlign: 'center' }}>ESTADO</span>
              </>}
            </div>
            {itemsSinBonif.map((item, i) => {
              const est = ESTADO_ITEM[item.estado_item] || ESTADO_ITEM.pendiente;
              const borderColor =
                item.estado_item === 'faltante' ? '#e63946' :
                item.estado_item === 'ok' ? '#4caf50' :
                ['diff_costo','diff_cantidad','diff_ambos'].includes(item.estado_item) ? '#ffc107' : 'transparent';
              return (
                <div key={item.id} style={{ ...s.tablaRow, background: i%2===0?'#1e1e2e':'#1a1a2e', borderLeft: `3px solid ${borderColor}` }}>
                  <span style={{ width: 120, color: '#4cc9f0', fontSize: 12, fontWeight: 'bold' }}>{item.codigo_bar || '—'}</span>
                  <span style={{ flex: 4, color: '#fff', fontSize: 12 }}>{item.descripcion}</span>
                  <span style={{ width: 70, textAlign: 'right', color: '#fff', fontWeight: 'bold' }}>{item.cantidad.toFixed(0)}</span>
                  <span style={{ width: 105, textAlign: 'right', color: '#aaa', fontSize: 12 }}>${item.costo_unitario.toFixed(4)}</span>
                  <span style={{ width: 100, textAlign: 'right', color: '#4cc9f0' }}>${item.costo_total.toFixed(2)}</span>
                  {yaVerificado && <>
                    <span style={{ width: 85, textAlign: 'right', fontSize: 12, color: item.diff_cantidad && Math.abs(item.diff_cantidad)>0.001?'#ff9800':'#aaa' }}>
                      {item.cantidad_morpheus !== null ? item.cantidad_morpheus?.toFixed(0) : '—'}
                      {item.diff_cantidad && Math.abs(item.diff_cantidad)>0.001 &&
                        <span style={{ color:'#ff9800', fontSize:10, marginLeft:4 }}>({item.diff_cantidad>0?'+':''}{item.diff_cantidad?.toFixed(0)})</span>}
                    </span>
                    <span style={{ width: 110, textAlign: 'right', fontSize: 12, color: item.diff_costo_pct>0.5?'#ffc107':'#aaa' }}>
                      {item.costo_morpheus !== null ? `$${item.costo_morpheus?.toFixed(4)}` : '—'}
                      {item.diff_costo_pct>0.5 &&
                        <span style={{ color:'#ffc107', fontSize:10, marginLeft:4 }}>({item.diff_costo_pct?.toFixed(1)}%)</span>}
                    </span>
                    <span style={{ width: 115, textAlign: 'center' }}>
                      <span style={{ background: est.color+'22', color: est.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 'bold' }}>
                        {est.label}
                      </span>
                    </span>
                  </>}
                </div>
              );
            })}
            {bonificaciones.length > 0 && <>
              <div style={{ padding: '8px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold' }}>
                BONIFICACIONES (total línea = $0)
              </div>
              {bonificaciones.map((item, i) => (
                <div key={item.id} style={{ ...s.tablaRow, background: '#1a2a1a', borderLeft: '3px solid #4caf50', opacity: 0.8 }}>
                  <span style={{ width: 120, color: '#4caf50', fontSize: 12 }}>{item.codigo_bar || '—'}</span>
                  <span style={{ flex: 4, color: '#aaa', fontSize: 12 }}>{item.descripcion}</span>
                  <span style={{ width: 70, textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>{item.cantidad.toFixed(0)}</span>
                  <span style={{ width: 105, textAlign: 'right', color: '#4caf50', fontSize: 12 }}>BONIF.</span>
                  <span style={{ width: 100, textAlign: 'right', color: '#4caf50' }}>$0.00</span>
                  {yaVerificado && <><span style={{width:85}}/><span style={{width:110}}/><span style={{width:115}}/></>}
                </div>
              ))}
            </>}
          </div>
        </div>
      </div>
    );
  }

  // ════ VISTA LISTA ════
  const POR_PAGINA     = 25;
  const totalPaginas   = Math.ceil(facturas.length / POR_PAGINA);
  const inicio         = (pagina - 1) * POR_PAGINA;
  const facturasPagina = facturas.slice(inicio, inicio + POR_PAGINA);

  const paginasVisibles = () => {
    let desde = Math.max(1, pagina - 2);
    let hasta  = Math.min(totalPaginas, desde + 4);
    desde = Math.max(1, hasta - 4);
    const rango = [];
    for (let i = desde; i <= hasta; i++) rango.push(i);
    return rango;
  };

  const Paginador = () => (
    <div style={s.paginacionRow}>
      <span style={s.totalInfo}>
        {facturas.length} facturas · mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, facturas.length)}
      </span>
      <div style={s.paginaBotones}>
        <button style={s.paginaBtn} onClick={() => setPagina(1)} disabled={pagina === 1}>«</button>
        <button style={s.paginaBtn} onClick={() => setPagina(pagina - 1)} disabled={pagina === 1}>‹</button>
        {paginasVisibles().map(p => (
          <button key={p}
            style={{ ...s.paginaBtn, ...(p === pagina ? s.paginaBtnActivo : {}) }}
            onClick={() => setPagina(p)}>
            {p}
          </button>
        ))}
        <button style={s.paginaBtn} onClick={() => setPagina(pagina + 1)} disabled={pagina === totalPaginas}>›</button>
        <button style={s.paginaBtn} onClick={() => setPagina(totalPaginas)} disabled={pagina === totalPaginas}>»</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.titulo}>📄 Facturas Proveedor</h2>
          <p style={s.subtitulo}>XML del SRI (principal) · PDF con Gemini AI (fallback) · verificación automática contra Morpheus</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button style={{
            background:'#2a1e00', border:'1px solid #ffc10755',
            borderRadius:8, color:'#ffc107', padding:'8px 16px',
            cursor:'pointer', fontSize:13, fontWeight:700,
          }} onClick={() => setModalReval(true)}>
            🔄 Re-validar
          </button>
          <button style={s.botonRefresh} onClick={cargarFacturas}>🔄 Actualizar</button>
        </div>
      </div>

      <div style={{ ...s.dropZone, ...(dragging ? s.dropZoneActivo : {}) }}
        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {subiendo ? (
          <div style={{ textAlign: 'center' }}>
            <div style={s.spinner} />
            <p style={{ color: '#4cc9f0', marginTop: 12, fontSize: 14 }}>Procesando archivos...</p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
            <p style={{ color: '#fff', fontWeight: 'bold', marginBottom: 4 }}>
              Arrastra aquí tus facturas o haz click para seleccionar
            </p>
            <p style={{ color: '#555', fontSize: 12, marginBottom: 12 }}>
              Puedes subir <strong style={{ color: '#4cc9f0' }}>varios archivos a la vez</strong>
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              <span style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 4, padding: '2px 10px', fontSize: 12, fontWeight: 'bold' }}>
                XML — SRI ⚡ recomendado
              </span>
              <span style={{ background: '#2a1a2a', color: '#f72585', borderRadius: 4, padding: '2px 10px', fontSize: 12, fontWeight: 'bold' }}>
                PDF — Gemini AI
              </span>
            </div>
            <input ref={inputRef} type="file" accept=".xml,.pdf" multiple style={{ display: 'none' }}
              onChange={e => e.target.files.length > 0 && subirArchivos(e.target.files)} />
            <button style={s.botonSeleccionar} onClick={() => inputRef.current?.click()}>
              Seleccionar archivos
            </button>
          </>
        )}
      </div>

      {errorMsg && <div style={s.errorBox}>{errorMsg}</div>}

      {modalReval && (
        <ModalRevalidacion
          facturas={facturas}
          onClose={() => setModalReval(false)}
          onCompletado={cargarFacturas}
        />
      )}

      {resultadoCarga && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitulo}>📊 Resultado de carga</span>
            <button style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 16 }}
              onClick={() => setResultadoCarga(null)}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✅ {resultadoCarga.resumen.nuevas} nuevas</span>
            <span style={{ color: '#ffc107', fontWeight: 'bold' }}>⚠️ {resultadoCarga.resumen.duplicadas} duplicadas</span>
            {resultadoCarga.resumen.errores > 0 &&
              <span style={{ color: '#e63946', fontWeight: 'bold' }}>❌ {resultadoCarga.resumen.errores} errores</span>}
          </div>
          {resultadoCarga.resultados.map((r, i) => (
            <div key={i} style={{
              ...s.tablaRow,
              background: i%2===0?'#1e1e2e':'#1a1a2e',
              borderLeft: `3px solid ${r.estado==='nueva'?'#4caf50':r.estado==='duplicada'?'#ffc107':'#e63946'}`,
            }}>
              <span style={{ flex: 2, color: '#aaa', fontSize: 12 }}>{r.archivo}</span>
              <span style={{ flex: 2, color: '#fff', fontSize: 13 }}>{r.nombre_proveedor || '—'}</span>
              <span style={{ flex: 2, color: '#4cc9f0', fontSize: 12 }}>{r.numero_factura || '—'}</span>
              <span style={{ width: 60 }}><OrigenBadge origen={r.origen} /></span>
              <span style={{ width: 110, textAlign: 'center' }}>
                <span style={{ color: r.estado==='nueva'?'#4caf50':r.estado==='duplicada'?'#ffc107':'#e63946', fontWeight:'bold', fontSize:12 }}>
                  {r.estado==='nueva'?'✅ Nueva':r.estado==='duplicada'?'⚠️ Duplicada':'❌ Error'}
                </span>
              </span>
              <span style={{ width: 80, textAlign: 'center' }}>
                {(r.estado==='nueva'||r.estado==='duplicada') && (
                  <button style={{ ...s.botonVer, ...(r.estado==='duplicada'?{borderColor:'#ffc107',color:'#ffc107'}:{}) }}
                    onClick={() => verDetalle(r.factura_id)}>
                    Ver →
                  </button>
                )}
                {r.mensaje && <span style={{ color:'#e63946', fontSize:11 }}>{r.mensaje}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {cargando ? (
        <p style={s.cargando}>Cargando facturas...</p>
      ) : facturas.length === 0 ? (
        <p style={{ color: '#555', textAlign: 'center', marginTop: 40 }}>No hay facturas. Sube tu primer XML o PDF.</p>
      ) : (
        <>
          {facturas.length > POR_PAGINA && <Paginador />}
          <div style={s.tabla}>
          <div style={s.tablaHeader}>
            <span style={{ flex: 2 }}>PROVEEDOR</span>
            <span style={{ width: 150, textAlign: 'center' }}>INGRESO</span>
            <span style={{ flex: 2 }}>N° FACTURA</span>
            <span style={{ width: 95,  textAlign: 'center' }}>FECHA</span>
            <span style={{ width: 65,  textAlign: 'center' }}>ÍTEMS</span>
            <span style={{ width: 55,  textAlign: 'center' }}>TIPO</span>
            <span style={{ width: 110, textAlign: 'right'  }}>TOTAL</span>
            <span style={{ width: 135, textAlign: 'center' }}>ESTADO</span>
            <span style={{ width: 55,  textAlign: 'center' }}>VER</span>
          </div>
          {facturasPagina.map((f, i) => {
            const est = ESTADO_FACTURA[f.estado] || ESTADO_FACTURA.pendiente;
            return (
              <div key={f.id}
                style={{ ...s.tablaRow, background: i%2===0?'#1e1e2e':'#1a1a2e', cursor: 'pointer' }}
                onClick={() => verDetalle(f.id)}
                onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                onMouseLeave={e => e.currentTarget.style.background = i%2===0?'#1e1e2e':'#1a1a2e'}
              >
                <span style={{ flex: 2 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{f.nombre_proveedor}</div>
                  <div style={{ color: '#555', fontSize: 11 }}>{f.ruc_proveedor}</div>
                </span>
                <span style={{ width: 150, textAlign: 'center' }}>
                  {f.ingreso_verificado ? (
                    <span style={{
                      background: '#1a3a2a', color: '#4caf50',
                      borderRadius: 6, padding: '3px 10px',
                      fontSize: 11, fontWeight: 'bold',
                      border: '1px solid #4caf50'
                    }}>
                    ✅ Ingreso Verificado
                    </span>
                  ) : (
                    <span style={{ color: '#333', fontSize: 11 }}>—</span>
                  )}
                </span>
                <span style={{ flex: 2, color: '#4cc9f0', fontSize: 13 }}>{f.numero_factura}</span>
                <span style={{ width: 95, textAlign: 'center', color: '#aaa', fontSize: 12 }}>{f.fecha_factura || '—'}</span>
                <span style={{ width: 65, textAlign: 'center', color: '#fff', fontWeight: 'bold' }}>{f.total_items}</span>
                <span style={{ width: 55, textAlign: 'center' }}><OrigenBadge origen={f.origen} /></span>
                <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>${f.total.toFixed(2)}</span>
                <span style={{ width: 135, textAlign: 'center' }}>
                  <span style={{ background: est.bg, color: est.color, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 'bold' }}>
                    {est.label}
                  </span>
                </span>
                <span style={{ width: 55, textAlign: 'center' }}>
                  <button style={s.botonVer} onClick={e => { e.stopPropagation(); verDetalle(f.id); }}>Ver →</button>
                </span>
              </div>
            );
          })}
        </div>
          {facturas.length > POR_PAGINA && (
            <div style={{ ...s.paginacionRow, marginTop: 16 }}>
              <span style={s.totalInfo}>Página {pagina} de {totalPaginas}</span>
              <div style={s.paginaBotones}>
                <button style={s.paginaBtn} onClick={() => setPagina(1)} disabled={pagina === 1}>«</button>
                <button style={s.paginaBtn} onClick={() => setPagina(pagina - 1)} disabled={pagina === 1}>‹</button>
                {paginasVisibles().map(p => (
                  <button key={p}
                    style={{ ...s.paginaBtn, ...(p === pagina ? s.paginaBtnActivo : {}) }}
                    onClick={() => setPagina(p)}>
                    {p}
                  </button>
                ))}
                <button style={s.paginaBtn} onClick={() => setPagina(pagina + 1)} disabled={pagina === totalPaginas}>›</button>
                <button style={s.paginaBtn} onClick={() => setPagina(totalPaginas)} disabled={pagina === totalPaginas}>»</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  headerRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  titulo:          { color: '#fff', marginBottom: 4 },
  subtitulo:       { color: '#555', fontSize: 13 },
  botonRefresh:    { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  botonVolver:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '6px 14px', cursor: 'pointer', fontSize: 13 },
  botonVerificar:  { background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  botonEliminar:   { background: 'none', border: '1px solid #3a1a1a', borderRadius: 8, color: '#e63946', padding: '8px 12px', cursor: 'pointer', fontSize: 13 },
  botonSeleccionar:{ background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '10px 24px', cursor: 'pointer', fontWeight: 'bold', fontSize: 14 },
  botonVer:        { background: 'none', border: '1px solid #4cc9f0', borderRadius: 6, color: '#4cc9f0', padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  cargando:        { color: '#aaa', padding: 40, textAlign: 'center' },
  dropZone:        { border: '2px dashed #2d2d44', borderRadius: 12, padding: 36, textAlign: 'center', marginBottom: 20, transition: 'border-color 0.2s, background 0.2s' },
  dropZoneActivo:  { borderColor: '#4cc9f0', background: '#0f1a2a' },
  spinner:         { width: 36, height: 36, border: '3px solid #2d2d44', borderTopColor: '#4cc9f0', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  errorBox:        { background: '#3a1a1a', border: '1px solid #e63946', borderRadius: 8, color: '#e63946', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  alertBox:        { background: '#2a2a1a', border: '1px solid #ffc107', borderRadius: 8, color: '#ffc107', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  estadoBadge:     { fontSize: 12, fontWeight: 'bold', padding: '4px 12px', borderRadius: 6 },
  kpisRow:         { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:         { background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 110, borderTop: '3px solid #7209b7' },
  kpiValor:        { fontSize: 24, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  kpiLabel:        { color: '#aaa', fontSize: 12 },
  card:            { background: '#1e1e2e', borderRadius: 12, padding: 24, marginBottom: 20 },
  cardHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitulo:      { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  tabla:           { background: '#1e1e2e', borderRadius: 12, overflow: 'hidden' },
  tablaHeader:     { display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
  tablaRow:        { display: 'flex', padding: '12px 16px', gap: 8, alignItems: 'center', borderBottom: '1px solid #1a1a2e', transition: 'background 0.15s' },
  paginacionRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  totalInfo:       { color: '#555', fontSize: 12 },
  paginaBotones:   { display: 'flex', gap: 4 },
  paginaBtn:       { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#aaa', padding: '6px 11px', cursor: 'pointer', fontSize: 13, minWidth: 34, textAlign: 'center' },
  paginaBtnActivo: { background: '#4cc9f0', border: '1px solid #4cc9f0', color: '#0f0f1a', fontWeight: 'bold' },
};

// Al final del archivo, DESPUÉS del objeto s = { ... };
const sModal = {
    overlay:    { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
    modal:      { background: '#1a1a2e', borderRadius: 16, border: '1px solid #2d2d44', width: '90%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #2d2d44', flexShrink: 0 },
    titulo:     { color: '#fff', fontWeight: 'bold', fontSize: 16 },
    subtitulo:  { color: '#4cc9f0', fontSize: 13, marginTop: 2 },
    cerrar:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', fontSize: 16, cursor: 'pointer', padding: '6px 12px' },
    body:       { overflowY: 'auto', flex: 1, padding: 24 },
    infoGrid:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 24px', marginBottom: 20, background: '#0f0f1a', borderRadius: 8, padding: 16 },
    infoItem:   { display: 'flex', flexDirection: 'column', gap: 4 },
    infoLabel:  { color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 },
    infoValor:  { color: '#fff', fontSize: 13 },
    totalesRow: { display: 'flex', gap: 16, marginBottom: 20 },
    totalItem:  { flex: 1, background: '#0f0f1a', borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 },
    tablaWrap:  { borderRadius: 8, overflow: 'hidden' },
    tablaHeader:{ display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
};